import { LensHitFilterState } from "../LensHitFilterState";
import { empty, nonEmpty } from "helpers/coll";
import { ClusterType } from "Backend";
import { BrowserMode } from "./BrowserMode";

export function loadQueryParams(
  params: URLSearchParams,
  lensHitFilterState: LensHitFilterState,
  setItemNum: (num: number) => void,
  setInitialExpandedCategory: (num: number) => void,
  setInitialSelectedDocs: (ids: number[]) => void,
  clearFilters: (clearRules?: boolean, clearFieldId?: boolean) => void,
  setMode: (mode: BrowserMode) => void,
  setQueryString: (s: string) => void,
  setQueryFieldContents: (s: string) => void
) {
  const fs = lensHitFilterState;
  const queryParams = params;
  const modeRaw = queryParams.get("mode");
  const mode = modeRaw === "discover" ? "discover" : "review";

  setMode(mode);

  const reviewTypeParam = queryParams.get("review_type");
  const theReviewType = reviewTypeParam === "chrono" ? "chrono" : "grouped";
  if (reviewTypeParam) {
    fs.setReviewType(theReviewType);
  }

  const fieldIdFilt = parseInt(queryParams.get("field_id") || "");
  if (fieldIdFilt) {
    // We found a field id in the query params on mount, we probably want that field with no
    // other filters applied
    fs.clearFilters(true);
    fs.setFormFieldId(fieldIdFilt);
  }

  const docFilts = queryParams.get("docs");
  if (docFilts && nonEmpty(docFilts)) {
    const docIds = docFilts.split(",").map((d) => parseInt(d));
    setInitialSelectedDocs(docIds);
  }

  const ruleFilts = queryParams.get("rules");
  if (ruleFilts && nonEmpty(ruleFilts)) {
    // We found rules in the query params on mount, we probably want those rules exclusively
    clearFilters(true, true);
    const rules = ruleFilts.split(",");
    rules.forEach((rule) => {
      lensHitFilterState.selectRule(rule);
    });
  }

  const sectionFilts = queryParams.get("records");
  if (sectionFilts && nonEmpty(sectionFilts)) {
    const sections = sectionFilts.split(",");
    sections.forEach((s) => {
      lensHitFilterState.selectSection(s);
    });
  }

  const notSectionFilts = queryParams.get("not_records");
  if (notSectionFilts && nonEmpty(notSectionFilts)) {
    const notSections = notSectionFilts.split(",").map((s) => s as ClusterType);
    fs.setSelectedNotClusterTypes(notSections);
  } else if (!(sectionFilts && nonEmpty(sectionFilts))) {
    if (mode === "review") {
      fs.setSelectedNotClusterTypes([ClusterType.FamilyHistory]);
    } else {
      fs.setSelectedNotClusterTypes([]);
    }
  } else {
    fs.setSelectedNotClusterTypes([]);
  }

  const textQuery = queryParams.get("query");
  if (textQuery) {
    setQueryString(textQuery);
    setQueryFieldContents(textQuery);
  }

  const hitNum = parseInt(queryParams.get("hit") || "") - 1 || 0;
  if (theReviewType === "chrono") {
    setItemNum(hitNum);
  }

  const categoryNum = parseInt(queryParams.get("group") || "");
  if (categoryNum || categoryNum === 0) {
    setInitialExpandedCategory(categoryNum - 1);
  }

  const hideNegated = queryParams.get("hide_negated") === "true";
  if (hideNegated) {
    lensHitFilterState.setShowNegated(false);
  } else {
    lensHitFilterState.setShowNegated(undefined);
  }
}

export function calculateQueryParams(
  fs: LensHitFilterState,
  itemNum: number,
  categoryNum: number | undefined,
  mode: BrowserMode,
  queryString: string | undefined
): string {
  const reviewMode = mode === "review";

  const withDocs = empty(fs.selectedIntakes) ? "?" : "?docs=" + fs.selectedIntakes.map((d) => d.id).join(",");
  const withHit = withDocs + "&hit=" + (itemNum + 1);
  const withCategory = categoryNum !== undefined ? withHit + "&group=" + (categoryNum + 1) : withHit;

  const records = [...(fs.selectedPageTypes as string[]), ...(fs.selectedClusterTypes as string[])];
  const withRecords = empty(records) ? withCategory : withCategory + "&records=" + records.join(",");
  const withNotRecords = empty(fs.selectedNotClusterTypes)
    ? withRecords
    : withRecords + "&not_records=" + (fs.selectedNotClusterTypes || []).join(",");
  const withDiscoverParam = reviewMode ? withNotRecords : withNotRecords + "&query=" + (queryString || "");
  const withReviewType = withDiscoverParam + ("&review_type=" + fs.reviewType);

  const rules = fs.selectedRuleIds;
  const withRules = empty(rules) ? withReviewType : withReviewType + "&rules=" + rules.join(",");

  const withMode = withRules + "&mode=" + mode;

  const withFieldId = fs.currentField?.id ? withMode + "&field_id=" + fs.currentField?.id : withMode;

  const withNegated = fs.showNegated === false ? withFieldId + "&hide_negated=true" : withFieldId;

  return withNegated;
}
